import React, { lazy, Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

// Layouts
import ErrorLayout from "@/layouts/Error";
import PricingAuditLayout from "@/layouts/PricingAudit";
import PresentationLayout from "@/layouts/Presentation";

// Guards
import AuthGuard from "@/components/guards/AuthGuard";
import ExternalUserGuard from "@/components/guards/ExternalUserGuard";
import CapabilityGuard from "@/components/guards/CapabilityGuard";
import GuestGuard from "@/components/guards/GuestGuard";
import PostLoginRedirect from "@/components/guards/PostLoginRedirect";

// Auth components
import Page404 from "@/pages/auth/Page404";
import NoPropertyAccess from "@/pages/auth/NoPropertyAccess";

// Tab slugs live outside the lazy page so naming them here doesn't bundle it.
import { PERFORMANCE_ANALYSIS_TABS } from "@/pages/performanceanalysis/tabs";

// Landing
import SignInPage from "@/layouts/SignInPage";

// Notifications
import NotificationsPage from "@/pages/notifications";

import { featureFlags } from "@/config";

// Active Leads and Events are lazy-loaded to keep the entry bundle small
const ActiveLeads = lazy(() => import("@/pages/activeleads"));
const Events = lazy(() => import("@/pages/events-ui-new/Default"));

// Pricing Audit — Radix/Tailwind pages. Lazy so they stay out of the entry chunk.
const PaIndex = lazy(() => import("@/pages/pricingaudit-ui-new/Default"));
const PaInstance = lazy(
  () => import("@/pages/pricingaudit-ui-new/Default/PricingInstanceRoute")
);
const PaConfig = lazy(
  () => import("@/pages/pricingaudit-ui-new/Configuration/ConfigurePage")
);

// OTA Audits — lazy-load to keep entry bundle small
const OtaAuditsDefault = lazy(() => import("./pages/otaaudits/Default"));

// Performance Analysis — lazy-load to keep entry bundle small
const PerformanceAnalysis = lazy(
  () => import("./pages/performanceanalysis/Default")
);

// Group Pricing Calculator — lazy-load to keep entry bundle small
const GroupPricingCalculator = lazy(
  () => import("@/pages/grouppricingcalculator")
);
const GpcPropertyConfig = lazy(
  () => import("@/pages/grouppricingcalculator/PropertyConfig")
);

// Group Pricing Requests — lazy-load to keep entry bundle small
const GroupPricingRequests = lazy(() => import("@/pages/grouppricingrequests"));

const PageLoader = (
  <Box
    sx={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size={24} />
  </Box>
);

// Forwards props so a route can hand its page static context (see the
// Performance Analysis tab routes below). Existing call sites pass none.
const withSuspense = <P extends object>(Component: React.ComponentType<P>) =>
  function Suspended(props: P) {
    // createElement rather than <Component {...props} />: spreading a generic P
    // through JSX trips TS's LibraryManagedAttributes inference.
    return (
      <Suspense fallback={PageLoader}>
        {React.createElement(Component, props)}
      </Suspense>
    );
  };

const SuspendedActiveLeads = withSuspense(ActiveLeads);
const SuspendedEvents = withSuspense(Events);
const SuspendedOtaAuditsDefault = withSuspense(OtaAuditsDefault);
const SuspendedPerformanceAnalysis = withSuspense(PerformanceAnalysis);
const SuspendedGroupPricingCalculator = withSuspense(GroupPricingCalculator);
const SuspendedGroupPricingRequests = withSuspense(GroupPricingRequests);
const SuspendedGpcPropertyConfig = withSuspense(GpcPropertyConfig);
const SuspendedPaIndex = withSuspense(PaIndex);
const SuspendedPaInstance = withSuspense(PaInstance);
const SuspendedPaConfig = withSuspense(PaConfig);

const routes = [
  {
    path: "/",
    element: (
      <GuestGuard>
        <PresentationLayout />
      </GuestGuard>
    ),
    children: [{ path: "", element: <SignInPage /> }],
  },
  {
    path: "auth/post-login",
    element: (
      <AuthGuard>
        <PostLoginRedirect />
      </AuthGuard>
    ),
  },
  {
    // Shared authenticated shell for every internal page. Kept as a single
    // pathless layout route (rather than one per section) so PricingAuditLayout
    // — and its sidebar collapsed/expanded state — mounts once and survives
    // navigation between sections instead of resetting on every click.
    element: (
      <AuthGuard>
        <PricingAuditLayout />
      </AuthGuard>
    ),
    children: [
      {
        // Internal-only pages — blocked for external users.
        element: (
          <ExternalUserGuard>
            <Outlet />
          </ExternalUserGuard>
        ),
        children: [
          {
            path: "pricing-audit-tool",
            children: [
              { path: "", element: <Navigate to="instances" replace /> },
              { path: "instances", element: <SuspendedPaIndex /> },
              // Sheet and activity log share one splat route so React Router
              // does not tear the match down when the URL changes — two sibling
              // routes would remount the instance view and flash. The component
              // reads the view off the pathname.
              {
                path: "instances/:instanceid/*",
                element: <SuspendedPaInstance />,
              },
              {
                path: "property-level-configs",
                element: <SuspendedPaConfig />,
              },
            ],
          },
          {
            path: "group-pricing-calculator",
            children: [
              { path: "", element: <SuspendedGroupPricingCalculator /> },
              {
                path: "property-config",
                element: <SuspendedGpcPropertyConfig />,
              },
            ],
          },
          ...(featureFlags.groupPricingRequests
            ? [
                {
                  path: "group-pricing-requests",
                  children: [
                    { path: "", element: <SuspendedGroupPricingRequests /> },
                  ],
                },
              ]
            : []),
          {
            path: "events",
            children: [
              { path: "", element: <SuspendedEvents /> },
              { path: ":propertyId", element: <SuspendedEvents /> },
            ],
          },
          ...(featureFlags.notifications
            ? [
                {
                  path: "notifications",
                  children: [{ path: "", element: <NotificationsPage /> }],
                },
              ]
            : []),
          {
            path: "ota-audits",
            children: [
              { path: "", element: <SuspendedOtaAuditsDefault /> },
              { path: ":propertyId", element: <SuspendedOtaAuditsDefault /> },
            ],
          },
        ],
      },
      // Contract-gated rather than internal-only: an external user reaches
      // these when their portfolio holds the matching capability.
      {
        path: "active-leads",
        children: [
          {
            path: "",
            element: (
              <CapabilityGuard requires="sales">
                <SuspendedActiveLeads />
              </CapabilityGuard>
            ),
          },
        ],
      },
      {
        path: "performance-analysis",
        element: (
          <CapabilityGuard requires="revenue">
            <Outlet />
          </CapabilityGuard>
        ),
        children: [
          // Bare path renders the page, which normalises the URL to
          // the default tab slug (keeping the query string).
          { path: "", element: <SuspendedPerformanceAnalysis /> },
          // One static child per tab, all rendering the same page —
          // rather than swapping an Outlet, so an already-loaded tab
          // stays mounted across switches. The tab travels as a prop
          // so the page never has to re-derive it from the pathname,
          // which cannot tell a slug from a same-named property.
          ...PERFORMANCE_ANALYSIS_TABS.map((tab) => ({
            path: tab.slug,
            element: <SuspendedPerformanceAnalysis tab={tab.slug} />,
          })),
          // Legacy deep links. Static siblings above outrank this in
          // React Router's matching, so a tab slug never lands here.
          {
            path: ":propertyId",
            element: <SuspendedPerformanceAnalysis />,
          },
        ],
      },
      // Never gated — every CapabilityGuard redirects here, so a gate on it
      // would loop.
      { path: "no-access", element: <NoPropertyAccess /> },
    ],
  },
  {
    path: "*",
    element: <ErrorLayout />,
    children: [{ path: "*", element: <Page404 /> }],
  },
];

export default routes;
